import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PageConfig } from 'c-config';
import { PostAuthRoutes } from 'c-routes';
import { PermissionName } from 'c-sdk';
import type {
    FilterableEntityTableProps,
    FilterableEntityTableRefAPI,
    RenderRowProps,
} from 'c-pagination';
import {
    FilterableEntityTable,
    FilterableEntityTableRendererType,
    readTableUrlState,
} from 'c-pagination';
import { FullPageEntityTableWrapper } from 'c-wrapper';
import { displaysListIcon } from 'c-main/icons';
import { displayScreenColumns } from 'c-displays/Components/DisplaysEntityColumns';
import ReactGA from 'react-ga';
import { useCommonTranslation } from 'c-translation';
import { alpha, Stack, Switch, Typography } from '@mui/material';
import { Button, CreateEntityButton, TextAreaField } from 'c-components';
import { useBoolean } from 'react-hanger';
import { useTheme } from '@mui/styles';
import { useLocation } from 'react-router-dom';
import { usePaginatedEntityData } from 'c-data';
import { uniq } from 'ramda';
import NewScreenForm from './DisplaysListPageComponents/NewScreenForm';
import ManualAudienceDialog from './DisplaysListPageComponents/ManualAudienceDialog';
import MassAllocationDialog from './DisplaysListPageComponents/MassAllocationDialog';
import AllocationDialog from './DisplaysListPageComponents/AllocationDialog';
import OperationalHoursUploadDialog from './DisplaysListPageComponents/OperationalHoursUploadDialog';

const textSearchCols = ['location', 'vendor_id', 'address', 'channel', 'lat', 'long'];
const paginationTag = 'displays-admin-displays-list';
const filterModeStorageKey = `${paginationTag}-filtering-display-id`;

const DisplaysListPage = () => {
    useEffect(() => {
        ReactGA.pageview(window.location.pathname + window.location.search);
    }, []);
    const [ids, setIds] = useState([]);
    const [allocationResponse, setResponse] = useState<any | null>(null);
    const [conflictedIds, setConflictedIds] = useState<number[] | null>(null);
    const kartFilter = useBoolean(false);
    const filtered = useBoolean(false);
    const massAllocationDialogState = useBoolean(false);
    const [deletedIds, setDeletedIds] = useState([]);
    const t = useCommonTranslation();
    const { paginatedData, reset: resetPaginatedList } = usePaginatedEntityData(
        paginationTag,
        'Displays_Screen',
    );
    const [frameIds, setFrameIds] = useState('');
    const paginatedIds = useMemo(
        () => paginatedData?.data.filter(id => !deletedIds.includes(id)),
        [paginatedData, deletedIds],
    );
    const filteringDisplayId = useBoolean(false);
    const filters = useMemo(() => paginatedData?.filters, [paginatedData]);
    const dialogState = useBoolean(false);
    const operationalHoursDialogState = useBoolean(false);
    const allocationState = useBoolean(false);

    const { search } = useLocation();
    const isFiltersHydrated = useRef(false);
    useEffect(() => {
        if (isFiltersHydrated.current) return;
        // A shared or refreshed URL wins over the store, as it does for the table: the table
        // restores the URL's filters after this first render, too late to read them from the store.
        const cachedFilters =
            readTableUrlState(search, paginationTag)?.filters ?? paginatedData?.filters;
        const cachedDisplayIds = cachedFilters?.['filter.id'] ?? [];
        const cachedFrameIds = cachedFilters?.['filter.frame_id'] ?? [];

        if (Array.isArray(cachedDisplayIds) && cachedDisplayIds.length > 0) {
            setFrameIds(cachedDisplayIds.join('\n'));
            filteringDisplayId.setTrue();
        } else if (Array.isArray(cachedFrameIds) && cachedFrameIds.length > 0) {
            setFrameIds(cachedFrameIds.join('\n'));
            filteringDisplayId.setFalse();
        } else {
            const stored = sessionStorage.getItem(filterModeStorageKey);
            if (stored != null) {
                filteringDisplayId.setValue(JSON.parse(stored));
            }
        }

        isFiltersHydrated.current = true;
    }, [paginatedData?.filters, filteringDisplayId, search]);

    useEffect(() => {
        sessionStorage.setItem(filterModeStorageKey, JSON.stringify(filteringDisplayId.value));
    }, [filteringDisplayId.value]);

    const tableRef = useRef<FilterableEntityTableRefAPI>();
    const compactTopButtonSx = useMemo(
        () => ({
            minHeight: 26,
            minWidth: 0,
            px: 1,
            py: 0.3,
            fontSize: '0.72rem',
            lineHeight: 1.1,
        }),
        [],
    );
    useEffect(() => {
        if (tableRef?.current) {
            tableRef.current.search(1);
        }
        if ((allocationResponse as any)?.data?.success === true) {
            tableRef.current.search(1);
        }
        if (frameIds) {
            tableRef.current.search(1);
        }
    }, [kartFilter.value, allocationResponse, frameIds]);

    const kartButton = useMemo(
        () => (
            <Button
                size="small"
                sx={compactTopButtonSx}
                disabled={ids.length === 0}
                onClick={kartFilter.toggle}
            >
                {kartFilter.value === false
                    ? t('Modules.Displays.DisplaysList.allocationDialog.viewSelected')
                    : t('Modules.Displays.DisplaysList.allocationDialog.viewAll')}
            </Button>
        ),
        [kartFilter, t, ids, compactTopButtonSx],
    );

    const clearSelectedButton = useMemo(
        () => (
            <Button
                size="small"
                sx={compactTopButtonSx}
                disabled={ids.length === 0}
                onClick={() => setIds([])}
            >
                {t('Modules.Displays.DisplaysList.clearSelected')}
            </Button>
        ),
        [ids.length, t, compactTopButtonSx],
    );

    const uniqueIds = useMemo(
        () =>
            uniq(
                frameIds
                    .split('\n')
                    ?.map(id => id.trim())
                    .filter(id => id.length > 0),
            ),
        [frameIds],
    );

    const theme = useTheme();
    const generateSx = useCallback<RenderRowProps<any>['generateSx']>(
        (row, id) => {
            const resp = (allocationResponse as any)?.data?.conflicts?.data?.map(id => id.id) ?? [];
            if (deletedIds.includes(id)) {
                return { opacity: 0.25 };
            }
            const exists = ids.includes(id);
            const showColor = exists ? resp?.includes(id) : false;
            if (!showColor) return {};

            return {
                bgcolor: alpha(theme.palette.error.light, 0.25),
            };
        },
        [theme, allocationResponse, ids, deletedIds],
    );

    const updateSearchPayload = useCallback<FilterableEntityTableProps['updateSearchPayload']>(
        payload => {
            const existingDisplayIds = payload?.filters?.['filter.id'] ?? [];
            const existingFrameIds = payload?.filters?.['filter.frame_id'] ?? [];
            const hasCachedIds =
                (Array.isArray(existingDisplayIds) && existingDisplayIds.length > 0) ||
                (Array.isArray(existingFrameIds) && existingFrameIds.length > 0);

            if (!isFiltersHydrated.current && !frameIds.trim() && hasCachedIds) {
                return payload;
            }

            return {
                ...payload,
                filters: {
                    ...payload.filters,
                    ...(filteringDisplayId.value
                        ? {
                              'filter.id': filtered.value
                                  ? conflictedIds
                                  : filteringDisplayId.value
                                  ? uniqueIds
                                  : kartFilter.value
                                  ? ids
                                  : [],
                          }
                        : {
                              'filter.frame_id': uniqueIds,
                              'filter.id': filtered.value
                                  ? conflictedIds
                                  : kartFilter.value
                                  ? ids
                                  : [],
                          }),
                },
            };
        },
        [
            ids,
            kartFilter.value,
            filtered.value,
            conflictedIds,
            uniqueIds,
            filteringDisplayId.value,
            frameIds,
        ],
    );

    useEffect(() => {
        if (ids.length === 0) {
            kartFilter.setFalse();
        }
    }, [ids.length, kartFilter]);

    const onChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => setFrameIds(e.target.value),
        [],
    );

    return (
        <FullPageEntityTableWrapper
            title="Modules.Displays.DisplaysList.metaTitle"
            controls={useMemo(
                () => [
                    <CreateEntityButton
                        entityName="Displays_Screen"
                        title={t('Modules.Displays.DisplaysList.createButton')}
                        form={<NewScreenForm />}
                        key="new-screen-button"
                        buttonLabel={t('Modules.Displays.DisplaysList.createButton')}
                        permissions={[PermissionName.DisplaysScreensCreate]}
                        redirectPath={PostAuthRoutes.Displays.DisplaysDisplayEdit}
                        maxWidth="xl"
                    />,
                    <MassAllocationDialog
                        key="mass-allocation-dialog"
                        dialogState={massAllocationDialogState}
                    />,
                    <ManualAudienceDialog key="manual-audience-dialog" dialogState={dialogState} />,
                    <OperationalHoursUploadDialog
                        key="operational-hours-dialog"
                        dialogState={operationalHoursDialogState}
                    />,
                    <AllocationDialog
                        key="allocation-dialog"
                        ids={ids}
                        setIds={setIds}
                        filters={filters}
                        setConflictedIds={setConflictedIds}
                        filtered={filtered}
                        allocationState={allocationState}
                    />,
                    kartButton,
                    clearSelectedButton,
                ],
                [
                    dialogState,
                    operationalHoursDialogState,
                    t,
                    kartButton,
                    massAllocationDialogState,
                    clearSelectedButton,
                    ids,
                    filters,
                    filtered,
                    allocationState,
                    setConflictedIds,
                ],
            )}
        >
            <FilterableEntityTable
                key={paginationTag}
                tag={paginationTag}
                ref={tableRef}
                columns={displayScreenColumns(setIds, ids, paginatedIds, setDeletedIds, deletedIds)}
                baseEntityName="Displays_Screen"
                textSearchColumns={textSearchCols}
                rendererType={FilterableEntityTableRendererType.Contained}
                revertToIdSearchOnNumberOnlyInput={false}
                orderBy="id"
                direction="desc"
                filters={{
                    scope: ['noClassic'],
                }}
                showFilters
                customFilters={useMemo(
                    () => [
                        <Stack key="frameIdFilter">
                            <Stack direction="row">
                                <Switch
                                    checked={filteringDisplayId.value}
                                    onChange={filteringDisplayId.toggle}
                                />
                                <Typography variant="body2" color="textSecondary">
                                    {t('Modules.Displays.DisplaysList.customFilters.toggle')}
                                </Typography>
                            </Stack>
                            <TextAreaField
                                value={frameIds}
                                onChange={onChange}
                                placeholder={
                                    filteringDisplayId.value
                                        ? t(
                                              'Modules.Displays.DisplaysList.customFilters.displayIds',
                                          )
                                        : t('Modules.Displays.DisplaysList.customFilters.frameIds')
                                }
                                showCharacterCount={false}
                            />
                            {uniqueIds?.length > 0 && (
                                <Typography fontSize="small" align="right">
                                    {filteringDisplayId.value
                                        ? t(
                                              'Modules.Displays.DisplaysList.customFilters.displaysCount',
                                              {
                                                  count: uniqueIds?.length,
                                              },
                                          )
                                        : t(
                                              'Modules.Displays.DisplaysList.customFilters.frameCount',
                                              {
                                                  count: uniqueIds?.length,
                                              },
                                          )}
                                </Typography>
                            )}
                        </Stack>,
                    ],
                    [filteringDisplayId, frameIds, onChange, t, uniqueIds?.length],
                )}
                customFilterResets={[
                    () => {
                        setFrameIds('');
                        filteringDisplayId.setFalse();
                        sessionStorage.removeItem(filterModeStorageKey);
                        resetPaginatedList();
                    },
                ]}
                generateRowSx={generateSx}
                updateSearchPayload={updateSearchPayload}
                displayDownloadButton
                reImportButton
                filterApplyButton
                includes={[
                    'energy_conversion_override',
                    'energy_uses_per_second',
                    'fileTypes',
                    'tags',
                    'operationalTimes',
                    'packs',
                    'markets',
                    'resolutions',
                    'displaysVendorIds',
                    'owner',
                    'softDeleted',
                ]}
            />
        </FullPageEntityTableWrapper>
    );
};

const setup: PageConfig = {
    id: 'DisplaysListPage',
    component: DisplaysListPage,
    disableDesktopContentScrolling: true,
    systemSearch: {
        title: 'Modules.Displays.DisplaysList.metaTitle',
        description: 'Modules.Displays.DisplaysList.description',
        icon: displaysListIcon,
        type: 'page',
        aliases: 'SystemSearch.aliases.displaysAdminDisplayList',
        route: PostAuthRoutes.Displays.DisplaysList,
        saveAsRecentPage: true,
        breadcrumbs: [
            { label: 'Modules.Admin.Home.metaTitle', link: PostAuthRoutes.Admin.AdminHome },
            {
                label: 'Modules.Displays.AdminHome.metaTitle',
                link: PostAuthRoutes.Displays.DisplaysAdminHome,
            },
            {
                label: 'Modules.Displays.DisplaysList.metaTitle',
            },
        ],
        permissions: [PermissionName.Admin],
    },
};

export default setup;
